import { useQuery } from '@tanstack/react-query';
import { Eye, EyeOff, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { SortableHeader, TablePager } from '@/components/SortableTable';
import { Badge, Button, Card, CardContent, CardHeader } from '@/components/ui';
import { sourcesApi } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useTableState } from '@/lib/use-table-state';
import type { Source, SourceType } from '@/types/api';

import { AddSourceDialog } from './AddSourceDialog';
import { SourceActions } from './SourceActions';

type Tab = 'all' | 'rss' | 'extension';

const TAB_FILTERS: Record<Tab, (s: Source) => boolean> = {
  all: () => true,
  rss: (s) => s.source_type === 'rss_feed',
  extension: (s) => s.source_type === 'browser_ext',
};

type SourceColumn =
  | 'source_url'
  | 'source_type'
  | 'retrieval_tier'
  | 'consecutive_failures'
  | 'last_crawled_at';

const COLUMNS = [
  { key: 'source_url' as const, accessor: (s: Source) => s.source_url.toLowerCase() },
  { key: 'source_type' as const, accessor: (s: Source) => s.source_type },
  { key: 'retrieval_tier' as const, accessor: (s: Source) => s.retrieval_tier },
  { key: 'consecutive_failures' as const, accessor: (s: Source) => s.consecutive_failures },
  { key: 'last_crawled_at' as const, accessor: (s: Source) => s.last_crawled_at ?? '' },
];

export function SourceRegistry() {
  const { t, i18n } = useTranslation(['studio', 'common']);
  const [params, setParams] = useSearchParams();
  const initialTab = (params.get('tab') as Tab) || 'all';
  const [activeTab, setActiveTab] = useState<Tab>(
    (['all', 'rss', 'extension'] as const).includes(initialTab) ? initialTab : 'all',
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Source | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['studio', 'sources', showInactive],
    queryFn: () => sourcesApi.list(showInactive),
  });

  const filtered = useMemo(() => (data ?? []).filter(TAB_FILTERS[activeTab]), [data, activeTab]);

  const tableState = useTableState<Source, SourceColumn>({
    rows: filtered,
    columns: COLUMNS,
    initialSort: { by: 'source_url', dir: 'asc' },
  });

  const sortable = (key: SourceColumn) => ({
    active: tableState.sort?.by === key,
    direction: tableState.sort?.dir,
    onClick: () => tableState.toggleSort(key),
  });

  const changeTab = (tab: Tab) => {
    setActiveTab(tab);
    if (tab === 'all') params.delete('tab');
    else params.set('tab', tab);
    setParams(params, { replace: true });
  };

  useEffect(() => {
    if (!successMessage) return;
    const id = setTimeout(() => setSuccessMessage(null), 4000);
    return () => clearTimeout(id);
  }, [successMessage]);

  const tabs: Tab[] = ['all', 'rss', 'extension'];

  return (
    <div className="space-y-4 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-display text-brand-900">{t('studio:sources.title')}</h1>
          <p className="mt-1 text-body text-slate-600">{t('studio:sources.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowInactive((v) => !v)}
            aria-pressed={showInactive}
          >
            {showInactive ? (
              <EyeOff className="mr-1 h-3.5 w-3.5" aria-hidden />
            ) : (
              <Eye className="mr-1 h-3.5 w-3.5" aria-hidden />
            )}
            {showInactive
              ? t('studio:sources.actions.hideInactive')
              : t('studio:sources.actions.showInactive')}
          </Button>
          <Button
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="mr-1 h-4 w-4" aria-hidden />
            {t('studio:sources.newSource')}
          </Button>
        </div>
      </header>

      {successMessage && (
        <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-small text-green-800">
          {successMessage}
        </div>
      )}

      <div className="flex gap-1 border-b border-slate-200">
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => changeTab(tab)}
            className={cn(
              'border-b-2 px-3 py-2 text-body transition-colors',
              activeTab === tab
                ? 'border-accent text-brand-900'
                : 'border-transparent text-slate-600 hover:text-slate-900',
            )}
            aria-pressed={activeTab === tab}
          >
            {t(`studio:sources.tabs.${tab}`)}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <h2 className="text-h2 text-brand-900">
            {data ? t('studio:sources.count', { count: filtered.length }) : '—'}
          </h2>
          {activeTab === 'extension' && (
            <p className="mt-1 text-small text-slate-600">
              {t('studio:sources.extensionHint')}
            </p>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && (
            <p className="px-4 py-3 text-small text-slate-500">{t('common:status.loading')}</p>
          )}
          {!isLoading && filtered.length === 0 && (
            <p className="px-4 py-6 text-center text-body text-slate-500">
              {activeTab === 'extension'
                ? t('studio:sources.noExtension')
                : t('studio:sources.noneRegistered')}
            </p>
          )}
          {filtered.length > 0 && (
            <>
              <table className="w-full text-body">
                <thead className="border-b border-slate-200 bg-slate-50 text-h4 text-slate-600">
                  <tr>
                    <SortableHeader
                      label={t('studio:sources.columns.url')}
                      {...sortable('source_url')}
                    />
                    <SortableHeader
                      label={t('studio:sources.columns.type')}
                      {...sortable('source_type')}
                    />
                    <SortableHeader
                      label={t('studio:sources.columns.tier')}
                      {...sortable('retrieval_tier')}
                    />
                    <SortableHeader label={t('studio:sources.columns.tags')} active={false} />
                    <SortableHeader
                      label={t('studio:sources.columns.failures')}
                      {...sortable('consecutive_failures')}
                    />
                    <SortableHeader
                      label={t('studio:sources.columns.lastCrawl')}
                      {...sortable('last_crawled_at')}
                    />
                    <th className="w-12 px-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tableState.page.map((source) => (
                    <tr
                      key={source.source_id}
                      className={cn(
                        'hover:bg-slate-50',
                        !source.active && 'opacity-60',
                      )}
                    >
                      <td className="max-w-md truncate px-4 py-2 font-mono text-small text-slate-700">
                        <a
                          href={source.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent-700 hover:text-accent"
                        >
                          {source.source_url}
                        </a>
                        {!source.active && (
                          <Badge variant="neutral" className="ml-2">
                            {t('studio:sources.inactive')}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <SourceTypeBadge type={source.source_type} />
                      </td>
                      <td className="px-4 py-2 text-small text-slate-700">
                        {source.retrieval_tier}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-1">
                          {source.topic_tags.length === 0 ? (
                            <span className="text-small text-slate-400">—</span>
                          ) : (
                            source.topic_tags.map((tag) => (
                              <Badge key={tag} variant="neutral">
                                {tag}
                              </Badge>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-small text-slate-700">
                        {source.consecutive_failures > 0 ? (
                          <Badge
                            variant={source.consecutive_failures >= 5 ? 'error' : 'warning'}
                          >
                            {source.consecutive_failures}
                          </Badge>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-2 text-small text-slate-500">
                        {source.last_crawled_at
                          ? new Date(source.last_crawled_at).toLocaleString(i18n.language)
                          : t('studio:sources.never')}
                        {source.source_type === 'rss_feed' && source.last_poll_at && (
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            <Badge
                              variant={
                                source.last_poll_status === 'ok'
                                  ? 'success'
                                  : source.last_poll_status === 'fetch_failed'
                                    ? 'warning'
                                    : source.last_poll_status === 'error'
                                      ? 'error'
                                      : 'info'
                              }
                            >
                              {t(
                                `studio:sources.poll.status.${source.last_poll_status ?? 'ok'}`,
                              )}
                            </Badge>
                            <span className="text-xs text-slate-500">
                              {t('studio:sources.poll.summary', {
                                new: source.last_poll_items_new ?? 0,
                                found: source.last_poll_items_found ?? 0,
                                ingested: source.last_poll_items_ingested ?? 0,
                              })}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <SourceActions
                          source={source}
                          onEdit={(s) => {
                            setEditing(s);
                            setDialogOpen(true);
                          }}
                          onMessage={setSuccessMessage}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="border-t border-slate-200">
                <TablePager
                  pageIndex={tableState.pageIndex}
                  pageCount={tableState.pageCount}
                  pageSize={tableState.pageSize}
                  total={tableState.total}
                  onPageChange={tableState.setPageIndex}
                  onPageSizeChange={tableState.setPageSize}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <AddSourceDialog
        open={dialogOpen}
        onOpenChange={(next) => {
          setDialogOpen(next);
          if (!next) setEditing(null);
        }}
        editing={editing}
        onSuccess={(mode) =>
          setSuccessMessage(
            mode === 'edit'
              ? t('studio:sources.edit.successMessage')
              : t('studio:sources.add.successMessage'),
          )
        }
      />
    </div>
  );
}

function SourceTypeBadge({ type }: { type: SourceType }) {
  const variant =
    type === 'browser_ext'
      ? 'accent'
      : type === 'rss_feed'
        ? 'info'
        : type.startsWith('manual')
          ? 'warning'
          : 'neutral';
  return <Badge variant={variant}>{type}</Badge>;
}
